<template>
  <v-card outlined class="outlined mb-6 elevation-0 rounded-lg">
    <v-toolbar color="transparent" flat class="text-h5 mb-2 rounded-0 py-1">
      <span v-if="!searchOn" data-testid="toolbar-title">New Leads</span>
      <v-layout v-else class="mt-4">
        <v-autocomplete data-testid="leads-location-filter-menu" class="leads-location-filter-menu pt-3 pb-2"
          align-with-title :items="locationCodes" v-model="filteredLocationCode" label="Filter by location code..."
          :hint="
            !filteredLocationCode
              ? 'Enter or select a location code'
              : 'Click X to reset the list'
          " persistent-hint clearable></v-autocomplete>
      </v-layout>
      <v-spacer />
      <v-btn data-testid="leads-search-btn" class="leads-search-btn" icon @click="toggleSearch">
        <v-icon>search</v-icon>
      </v-btn>
    </v-toolbar>
    <v-data-table :loading="loading" :headers="headers" :items="selectedContacts" no-data-text="No leads found"
      loading-text="Loading leads...">
      <template v-slot:[`item.customer_first_name`]="{ item }">
        <td data-testid="lead-name">
          {{ item.customer_first_name }} {{ item.customer_last_name }}
        </td>
      </template>
      <template v-slot:[`item.phone`]="{ item }">
        <td data-testid="lead-phone">{{ addDashes(item.customer_phone) }}</td>
      </template>
      <template v-slot:[`item.facility.location_code`]="{ item }">
        <td data-testid="lead-location-code">
          {{ item.facility.location_code }}
        </td>
      </template>
      <template v-slot:[`item.move_in_date`]="{ item }">
        <td data-testid="lead-move-in">{{ formatDate(item.move_in_date) }}</td>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn data-testid="leads-call-modal-btn" class="leads-call-modal-btn primary--text" small
          @click.stop="openCallModal(item)" :disabled="phoneActive">
          <v-icon small>phone</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <application-modal v-if="selectedContact" v-model="initCallModal" @collapse="clearContact"
      data-testid="init-call-modal" :width="width">
      <div slot="modal-title">Initiate Lead Call</div>
      <div slot="modal-body" style="min-height: 25rem">
        <v-tabs show-arrows v-model="tab">
          <v-tab data-testid="leads-contact-details-tab" class="leads-contact-details-tab">
            Contact Details
          </v-tab>
          <v-tab data-testid="leads-contact-notes-tab" class="leads-contact-notes-tab">
            Contact Notes
          </v-tab>
          <v-tab data-testid="leads-add-note-tab" class="leads-add-note-tab">
            Add Note
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item eager>
            <v-layout align-start class="my-4">
              <v-layout column class="subheading" style="width: 650px">
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Name: </v-flex>
                  <v-flex xs6>{{ selectedContact.customer_first_name }}
                    {{ selectedContact.customer_last_name }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Phone Number: </v-flex>
                  <v-flex xs6>{{
                      addDashes(selectedContact.customer_phone)
                  }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Email: </v-flex>
                  <v-flex xs6>{{ selectedContact.customer_email }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Reserved On: </v-flex>
                  <v-flex xs6>{{
                      formatDate(selectedContact.created_at)
                  }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Move-in Date:</v-flex>
                  <v-flex>{{
                      formatDate(selectedContact.move_in_date)
                  }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Online Rental</v-flex>
                  <v-flex>
                    <span v-if="selectedContact.rented_online == 1">Yes</span>
                    <span v-else>No</span>
                  </v-flex>
                </v-layout>
                <v-form ref="form" class="mt-4 ml-1" style="width: 515px">
                  <app-input data-testid="leads-call-id-input" class="leads-call-id-input" v-model="callId"
                    :type="'number'" label="Call ID" placeholder="Enter Call ID..." outlined
                    :rules="{ required: true }"></app-input>
                </v-form>
                <v-layout class="ml-1">
                  <transition name="slide-x-transition" mode="out-in">
                    <v-flex v-if="!emailLeadInput" key="button">
                      <v-btn data-testid="leads-open-email-input-btn"
                        class="leads-open-email-input-btn white--text ml-0" color="teal lighten-1" :elevation="22" large
                        raised @click="openEmailLeadInput">
                        Email Reservation
                      </v-btn>
                    </v-flex>
                    <v-layout v-else key="input">
                      <v-flex xs9>
                        <v-form ref="lead-form" style="height: 55px">
                          <app-input data-testid="leads-email-reservation-input" class="leads-email-reservation-input"
                            v-model="customerEmail" :type="'email'" label="Email"
                            placeholder="Enter Customer's Email..." outlined
                            :rules="{ required: true, email: true }"></app-input>
                        </v-form>
                      </v-flex>
                      <v-flex xs1 class="mr-2 mt-2 pt-1">
                        <v-btn data-testid="leads-email-reservation-btn" class="leads-email-reservation-btn"
                          color="teal lighten-1" fab bottom :loading="emailLoading" :disabled="!validEmail"
                          @click="sendReservationConfirmationEmail">
                          <v-icon>mail_outline</v-icon>
                        </v-btn>
                      </v-flex>
                      <v-flex xs1 class="mt-2 pt-1">
                        <v-btn data-testid="leads-close-email-input-btn" class="leads-close-email-input-btn"
                          color="red darken-2" fab bottom @click="emailLeadInput = false">
                          <v-icon>close</v-icon>
                        </v-btn>
                      </v-flex>
                    </v-layout>
                  </transition>
                </v-layout>
              </v-layout>
              <v-layout column class="subheading" style="width: 500px">
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Unit Name: </v-flex>
                  <v-flex>{{ unitFeatures.type_name }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Unit Type: </v-flex>
                  <v-flex>
                    <span v-if="parkingCheck(selectedContact.unit_dimensions)">Parking</span>
                    <span v-else>Storage</span>
                  </v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Unit Size: </v-flex>
                  <v-flex>{{ selectedContact.unit_dimensions }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Quoted Price: </v-flex>
                  <v-flex>${{
                      parseFloat(selectedContact.quoted_price).toFixed(2)
                  }}/month</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Promotion Offered: </v-flex>
                  <v-flex>{{ promotion_text }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Unit Features: </v-flex>
                  <v-layout column>
                    <v-flex v-for="(feature, i) of unitFeatures.features" :key="i" xs6>{{ feature }}</v-flex>
                  </v-layout>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Facility: </v-flex>
                  <v-flex>{{ selectedContact.facility.name }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Location Code: </v-flex>
                  <v-flex>{{ selectedContact.facility.location_code }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Facility Phone: </v-flex>
                  <v-flex>{{
                      addDashes(selectedContact.facility.phone)
                  }}</v-flex>
                </v-layout>
                <v-layout class="mx-2 my-1">
                  <v-flex xs4>Address: </v-flex>
                  <v-flex>{{ selectedContact.facility.street_address }}</v-flex>
                </v-layout>
                <v-layout class="mx-2">
                  <v-flex xs4 />
                  <v-flex>
                    {{ selectedContact.facility.city }},
                    {{ selectedContact.facility.state }}
                    {{ selectedContact.facility.postal_code }}
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-layout>
          </v-tab-item>
          <v-tab-item eager>
            <v-layout v-if="noteLoading" justify-center>
              <v-progress-circular :size="60" :width="6" indeterminate class="my-3" />
            </v-layout>
            <v-layout v-else-if="!noteLoading && notes.length" column style="
                max-height: 400px;
                outline: 1px grey solid;
                overflow-y: auto;
              " class="mt-4 pt-3 mb-3">
              <span class="ml-3 mt-23mb-2 text-h6">
                Notes for {{ selectedContact.customer_first_name }}
                {{ selectedContact.customer_last_name }}:
              </span>
              <v-divider class="mx-3 mt-1" />
              <v-layout column>
                <v-flex v-for="(note, i) of notes" :key="i" class="mx-3 my-2">
                  <v-layout column class="my-1">
                    {{ note.description }}
                    <v-layout class="mt-1" align-center>
                      <v-icon class="mr-1" size="medium">create</v-icon>
                      <span class="text-caption">Created on {{ formatDateAndTime(note.created_at) }}</span>
                    </v-layout>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-layout>
            <v-layout v-else class="pa-2 mt-3 text-h6">
              There are no notes for {{ selectedContact.first_name }}
              {{ selectedContact.last_name }}.
            </v-layout>
          </v-tab-item>
          <v-tab-item eager>
            <v-textarea data-testid="leads-add-note-textarea" class="leads-add-note-textarea px-2 pt-4" filled
              v-model="note" label="Add Note"
              hint="When you are finished, click 'Add Note' to save this note to this tenant's account." />
            <v-layout justify-end>
              <v-btn data-testid="leads-add-note-btn" class="leads-add-note-btn success" small @click="addNote"
                :loading="noteLoading">
                Add Note
              </v-btn>
            </v-layout>
          </v-tab-item>
        </v-tabs-items>
      </div>
      <v-btn data-testid="leads-begin-call-btn" class="leads-begin-call-btn primary" slot="create-button"
        :loading="loading" :disabled="phoneActive || !callId" @click="handleLeadCall(selectedContact)">
        Call
      </v-btn>
    </application-modal>
  </v-card>
</template>

<script>
/* eslint-disable eqeqeq */
import { emailRules, inputRules } from "@/helpers/form-rules";
import ApplicationModal from "@/components/ApplicationModal";
import { leadsCardHeaders } from "@/helpers/dashboard/leads-card-data-table-headers";
import moment from "moment";

export default {
  data() {
    return {
      loading: false,
      noteLoading: false,
      unitFeaturesLoading: false,

      initCallModal: false,
      submitted: false,
      searchOn: false,

      emailLeadInput: false,
      emailLoading: false,
      customerEmail: "",

      callId: "",
      facilityId: "",
      selectedContact: "",
      filteredLocationCode: "",
      notes: [],
      note: "",
      unitFeatures: "",

      emailRules: emailRules,
      inputRules: inputRules,

      width: "66%",
      headers: leadsCardHeaders,

      leads: [],
      leadsUpdate: null,

      tab: 0
    };
  },

  methods: {
    getAllLeads() {
      this.loading = true;
      this.$store
        .dispatch("getAllLeads")
        .then((resp) => {
          this.leads = resp.data;
          this.loading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    openCallModal(contact) {
      this.initCallModal = true;
      this.facilityId = contact.facility.id;

      let payload = {
        facilityId: this.facilityId,
        facility_id: this.facilityId,
        waiting_list_id: contact.waiting_list_id,
        unit_id: contact.unit_id
      };

      this.selectedContact = { ...contact, facility_id: contact.facility.id };
      this.getNotes(payload);
      this.getUnitFeatures(payload);
    },

    openEmailLeadInput() {
      this.emailLeadInput = true;
      this.customerEmail = this.selectedContact.customer_email;
    },

    toggleSearch() {
      if (this.searchOn === true) {
        this.searchOn = false;
      } else {
        this.searchOn = true;
      }
    },

    filteredArray(locationCode, array) {
      return locationCode
        ? array.filter(
          (index) => index["facility"]["location_code"] == locationCode
        )
        : array;
    },

    parkingCheck(unit) {
      return unit.charAt(0) == 0;
    },

    clearContact() {
      this.callId = "";
      this.facilityId = "";
      this.selectedContact = "";
      this.notes = [];
      this.note = "";
      this.$refs.form.reset();
    },

    formatDateAndTime(date) {
      return moment(date).format("MM/DD/YYYY, h:mm a");
    },

    formatDate(date) {
      return moment(date).utc().format("MM/DD/YYYY");
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    getNotes(payload) {
      this.noteLoading = true;

      this.$store
        .dispatch("getNotes", payload)
        .then((resp) => {
          this.notes = this.sortNotes(resp.data);
          this.noteLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.noteLoading = false;
        });
    },

    getUnitFeatures(payload) {
      this.unitFeaturesLoading = true;
      this.$store
        .dispatch("getUnitFeatures", payload)
        .then((resp) => {
          this.unitFeatures = resp.data;
          this.unitFeaturesLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.unitFeaturesLoading = false;
        });
    },

    addNote() {
      this.noteLoading = true;

      let payload = {
        facility_id: this.selectedContact.facility_id,
        waiting_list_id: this.selectedContact.waiting_list_id,
        description: this.note
      };

      this.$store
        .dispatch("addNote", payload)
        .then((resp) => {
          this.noteLoading = false;
          this.$store.dispatch("handleSuccess", "Reservation note added!");
          this.note = "";
          this.notes = this.sortNotes(resp.data);
        })
        .catch((err) => {
          this.noteLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    handleLeadCall(contact) {
      let payload = {
        ctm_id: parseFloat(this.callId),
        call_type: "lead",
        location_code: contact.facility.location_code,
        tenant_id: contact.tenant_id,
        external_reservation_id: contact.waiting_list_id
      };

      this.loading = true;

      this.$store
        .dispatch("beginCall", payload)
        .then((resp) => {
          let localStoragePayload = {
            locationCode: null,
            reservationId: contact.id,
            callId: resp.data.call.id,
            ctm_id: payload.ctm_id,
            call_type: payload.call_type,
            router: {
              name: "tenantDetails",
              facilityId: resp.facilityId
            },
            contact_id: contact.id,
            tenant_id: null
          };

          this.$store.dispatch("routeCall", localStoragePayload);

          this.loading = false;
          this.submitted = true;
          this.clearContact();
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    sortNotes(notes) {
      return notes.sort((a, b) => {
        let date1 = new Date(a.created_at).getTime();
        let date2 = new Date(b.created_at).getTime();
        return date2 - date1;
      });
    },

    sendReservationConfirmationEmail() {
      this.emailLoading = true;

      let payload = {
        external_reservation_id: this.selectedContact.waiting_list_id,
        facility_id: this.selectedContact.facility_id
      };

      if (this.customerEmail !== this.selectedContact.customer_email) {
        payload.email_address = this.customerEmail;
      }

      this.$store
        .dispatch("sendReservationConfirmationEmail", payload)
        .then((resp) => {
          this.emailLoading = false;
          this.$store.dispatch("handleSuccess", "Email sent!");
          this.emailLeadInput = false;
        })
        .catch((err) => {
          this.emailLoading = false;
          this.$store.dispatch("handleError", err);
          this.emailLeadInput = false;
        });
    }
  },

  computed: {
    promotion_text() {
      const contact = this.selectedContact;
      if (contact && contact.concession_id > -1 && contact.facility.promotions) {
        const promotionIndex = contact.facility.promotions.findIndex(promo => promo.concession_id == contact.concession_id);
        return promotionIndex > -1 ? contact.facility.promotions[promotionIndex].text : "";
      }
      return "";
    },
    locationCodes() {
      return this.leads.map((contact) => contact.facility.location_code);
    },

    selectedContacts() {
      return this.filteredArray(this.filteredLocationCode, this.leads);
    },

    phoneActive() {
      return localStorage.getItem("callStarted");
    },

    validEmail() {
      let regex = /^\S+@\S+\.\S+$/;
      return regex.test(this.customerEmail);
    }
  },

  created() {
    if (sessionStorage.getItem("access_token")) {
      this.getAllLeads();
      this.leadsUpdate = setInterval(() => {
        this.getAllLeads();
      }, 300000); // check for new leads every 5 min
    }
  },

  beforeDestroy() {
    clearInterval(this.leadsUpdate);
  },

  components: {
    ApplicationModal
  }
};
</script>
